import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

export interface ClientDetailsV2CopyDayDialogProps {
  open: boolean;
  onClose: () => void;
  /** Day number used as the copy source (e.g. Day 1 copied to selected days). */
  targetDayNumber: number;
  /** Display label e.g. "Day 4" or custom name. */
  targetDayLabel: string;
  /** Days available as copy targets (exclude the source day in the parent). */
  copyFromOptions: Array<{ id: string; name: string; detail?: string }>;
  onSubmit: (payload: { sourceDay: number; targetDays: number[] }) => void | Promise<void>;
  isSubmitting?: boolean;
}

const parsePickerDetail = (detail: string | undefined) => {
  const normalized = (detail || '').trim();
  if (!normalized) return { kcalLabel: '', metaLabel: '' };

  const match = normalized.match(/^(\d+(?:\.\d+)?)\s*kcal\s*·\s*(.*)$/i);
  if (!match) return { kcalLabel: '', metaLabel: normalized };

  const kcalLabel = `${match[1]} kcal`;
  const metaLabel = (match[2] || '').trim();
  return { kcalLabel, metaLabel };
};

/**
 * Copy one source day onto one or more selected target days in a single request.
 */
export const ClientDetailsV2CopyDayDialog: React.FC<ClientDetailsV2CopyDayDialogProps> = ({
  open,
  onClose,
  targetDayNumber,
  targetDayLabel,
  copyFromOptions,
  onSubmit,
  isSubmitting = false,
}) => {
  const [templateSearch, setTemplateSearch] = React.useState('');
  const [selectedCopyFromIds, setSelectedCopyFromIds] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setTemplateSearch('');
    setSelectedCopyFromIds([]);
  }, [open, copyFromOptions]);

  const activePickerOptions = React.useMemo(() => {
    return copyFromOptions.map((opt) => {
      const parsed = parsePickerDetail(opt.detail);
      return {
        id: opt.id,
        name: opt.name,
        kcalLabel: parsed.kcalLabel,
        metaLabel: parsed.metaLabel,
      };
    });
  }, [copyFromOptions]);

  const filteredTemplates = React.useMemo(() => {
    const needle = templateSearch.trim().toLowerCase();
    return activePickerOptions.filter((option) => {
      if (!needle) return true;
      return option.name.toLowerCase().includes(needle);
    });
  }, [activePickerOptions, templateSearch]);

  const handleSubmit = React.useCallback(async () => {
    const selectedTargetDays = selectedCopyFromIds
      .map((id) => Number(id))
      .filter((day) => Number.isFinite(day) && day > 0);
    if (!selectedTargetDays.length) return;
    await onSubmit({
      sourceDay: targetDayNumber,
      targetDays: selectedTargetDays,
    });
  }, [onSubmit, selectedCopyFromIds, targetDayNumber]);

  const canSubmit = selectedCopyFromIds.length > 0;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) onClose();
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 'min(92vw, 560px)' },
          maxWidth: 560,
          borderRadius: '16px',
          bgcolor: '#fff',
          m: { xs: 1.5, sm: 2 },
          maxHeight: { xs: 'calc(100% - 24px)', sm: 'calc(100% - 48px)' },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2.2, sm: 2.6 }, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1.75rem', fontWeight: 600, color: '#111827', lineHeight: 1.1 }}>
              Copy day
            </Typography>
            <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#6b7280' }}>
              Copy meals from{' '}
              <Box component="span" sx={{ fontWeight: 600, color: '#344054' }}>
                {targetDayLabel}
              </Box>{' '}
              to selected days
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              width: 28,
              height: 28,
              border: '1px solid #d0d5dd',
              color: '#98a2b3',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2.2, border: '1px solid #dbe3eb', borderRadius: '10px', px: 1.1, py: 1, bgcolor: '#f7f9fc' }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Source day
          </Typography>
          <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '0.92rem', fontWeight: 600, color: '#1f2937' }}>
            {targetDayLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 1.9,
            border: '1px solid #dbe3eb',
            borderRadius: '10px',
            height: 40,
            px: 1.1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.9,
            bgcolor: '#f7f9fc',
          }}
        >
          <SearchRoundedIcon sx={{ fontSize: 18, color: '#9aa8b8' }} />
          <InputBase
            value={templateSearch}
            onChange={(event) => setTemplateSearch(event.target.value)}
            placeholder="Search target day"
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                p: 0,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.92rem',
                color: '#667085',
              },
            }}
          />
        </Box>

        <Typography sx={{ mt: 1.8, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#1f2937' }}>
          Choose target day(s)
        </Typography>

        <Box sx={{ mt: 0.9, border: '1px solid #e0e7ef', borderRadius: '10px', overflow: 'hidden', bgcolor: '#fff' }}>
          <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {filteredTemplates.length ? (
              filteredTemplates.map((template, index) => {
                const selectedTemplate = selectedCopyFromIds.includes(template.id);
                return (
                  <Box
                    key={template.id}
                    onClick={() => {
                      if (isSubmitting) return;
                      setSelectedCopyFromIds((prev) =>
                        prev.includes(template.id)
                          ? prev.filter((id) => id !== template.id)
                          : [...prev, template.id]
                      );
                    }}
                    sx={{
                      px: 1.1,
                      py: 1.0,
                      minHeight: 56,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: index === filteredTemplates.length - 1 ? 'none' : '1px solid #ecf1f6',
                      bgcolor: selectedTemplate ? '#f6fbff' : '#fff',
                      cursor: isSubmitting ? 'default' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    <Typography
                      sx={{
                        minWidth: 0,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.84rem',
                        fontWeight: 500,
                        color: '#475467',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {template.name}
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 0,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.84rem',
                          fontWeight: 700,
                          color: '#111827',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flexShrink: 0,
                        }}
                      >
                        {template.kcalLabel || ''}
                      </Typography>

                      <Typography
                        sx={{
                          minWidth: 0,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          color: '#98a2b3',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {template.metaLabel || ''}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '6px',
                        border: selectedTemplate ? '1.5px solid #20aedc' : '1.5px solid #d5dde6',
                        bgcolor: selectedTemplate ? '#20aedc' : '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selectedTemplate ? <CheckRoundedIcon sx={{ fontSize: 15, color: '#fff' }} /> : null}
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ px: 1.1, py: 1.1 }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#98a2b3' }}>
                  {copyFromOptions.length === 0
                    ? 'No other days in this plan to copy from. Add another day first.'
                    : 'No days match your search.'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 1.8, borderTop: '1px solid #ecf1f6' }} />

        <Box sx={{ mt: 2.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              minWidth: 100,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#9aa3af',
              px: 0.4,
              '&:hover': { bgcolor: 'transparent', color: '#8893a0' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || isSubmitting}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={14} sx={{ color: '#fff' }} />
              ) : (
                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              minHeight: 34,
              minWidth: 120,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              bgcolor: '#2aabd7',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#249cc5', boxShadow: 'none' },
            }}
          >
            Copy meals
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
